import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { BrainCircuit, Sparkles, Info } from 'lucide-react';
import ModelSelector from './ModelSelector';
import { useAIModels } from '../hooks/useAIModels';
import { useAuth } from '../context/AuthContext';

const GlobalModelSelector = ({ compact = false }) => {
  const { authenticatedRequest } = useAuth();
  const { getAuxiliaryModel } = useAIModels(authenticatedRequest);
  const [selectedModel, setSelectedModel] = useState('');

  useEffect(() => {
    const validModel = getAuxiliaryModel();
    if (validModel && validModel !== selectedModel) {
      setSelectedModel(validModel);
    }
  }, [getAuxiliaryModel]);

  const handleModelChange = (event) => {
    const newModel = event.target.value;
    setSelectedModel(newModel);
    localStorage.setItem('globalAiModel', newModel);
  };

  if (!selectedModel) return null;

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: 'transparent',
      }}
    >
      {!compact && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BrainCircuit size={14} color="#3B82F6" strokeWidth={1.5} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: '0.65rem',
                letterSpacing: '0.05em',
                color: '#4B5563',
                textTransform: 'uppercase',
              }}
            >
              AI Model
            </Typography>
          </Box>
          <Tooltip
            title="This model will be used for secondary tasks like generating image prompts and enhancing user inputs"
            placement="top"
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'help',
              }}
            >
              <Info size={12} color="#6B7280" strokeWidth={1.5} />
            </Box>
          </Tooltip>
        </Box>
      )}
      <ModelSelector
        icon={<Sparkles size={16} color="#3B82F6" />}
        label={compact ? "" : "Select Model"}
        value={selectedModel}
        onChange={handleModelChange}
        variant={compact ? "compact" : "sidebar"}
        showDescription={!compact}
        compact={compact}
        smallFont={true}
        sx={{
          backgroundColor: 'transparent',
          '& .MuiSelect-select': {
            py: compact ? 0.75 : 1,
            fontWeight: 500,
            fontSize: '0.85rem',
            color: '#1F2937',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: 'white',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.08)',
            borderRadius: '8px',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(59, 130, 246, 0.3)',
          },
        }}
      />
    </Box>
  );
};

export default GlobalModelSelector;